import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { useState, useMemo } from "react";
import AnalyticsService from "@/services/analytics";
import { CaretRightIcon, CaretDownIcon, FolderDottedIcon } from "@phosphor-icons/react";
import { MapPinIcon, Search, XIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

type PublicContentProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  visibleSpaceIds?: Set<number>;
};

function PublicContent({
  venueDetailsResponse,
  visibleSpaceIds,
}: PublicContentProps) {
  const i18n = useI18N();
  const { user } = useAuth();
  const isMapper = !!(user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const searchTerm = localSearchTerm;

  const [selectedActionTitle, setSelectedActionTitle] = useState<string>("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "location">(
    "newest",
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Get public note actions from venue details
  const noteActions = useMemo(() => {
    return venueDetailsResponse?.details?.businessNotesActionsVenueNotes || [];
  }, [venueDetailsResponse]);

  // Get public notes, filtered to spaces visible in this custom view
  const publicNotes = useMemo(() => {
    const all = venueDetailsResponse?.details?.businessNotesAll || [];
    // If no space visibility constraint, return everything
    if (!visibleSpaceIds || visibleSpaceIds.size === 0) return all;
    return all.filter(
      (note) =>
        // Whole-venue alerts are always shown
        note.location_type === "Business" ||
        // Space-specific alerts only if the space is visible in this view
        visibleSpaceIds.has(note.location_id),
    );
  }, [venueDetailsResponse, visibleSpaceIds]);

  // Filter note actions to only show those with notes
  const noteActionsWithNotes = useMemo(() => {
    return noteActions.filter((action) => {
      const notesCount = publicNotes.filter(
        (note) => note.action_title === action.title,
      ).length;
      return notesCount > 0;
    });
  }, [noteActions, publicNotes]);

  // Set initial action title
  useMemo(() => {
    if (noteActionsWithNotes.length > 0 && !selectedActionTitle) {
      setSelectedActionTitle(noteActionsWithNotes[0].title);
    }
  }, [noteActionsWithNotes, selectedActionTitle]);

  // Scroll to selected section
  const scrollToActionSection = (actionTitle: string) => {
    setSelectedActionTitle(actionTitle);
    // Hide mobile menu when filter is selected
    setShowMobileMenu(false);

    // Add a small delay to ensure the state is updated
    setTimeout(() => {
      const sectionId = `action-section-${actionTitle.toLowerCase().replace(/\s+/g, "-")}`;
      const element = document.getElementById(sectionId);
      if (element) {
        // Find the scrollable container
        const container = element.closest(".overflow-y-auto");

        if (container) {
          // Get accurate positioning using getBoundingClientRect
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          // Calculate the element's position relative to the scrollable container
          const relativeTop =
            elementRect.top - containerRect.top + container.scrollTop;

          // Scroll to position the section title near the top with proper padding
          const scrollTop = relativeTop - 32;

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        } else {
          // Fallback: use browser's scrollIntoView
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }, 50);
  };

  // Filter notes based on search term â€” searches across ALL notes regardless of sidebar selection
  const filteredNotes = useMemo(() => {
    if (!searchTerm) return publicNotes;

    // Split on whitespace into words, drop empty strings
    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);

    return publicNotes.filter((note) => {
      const combinedText = [
        note.business_note_text,
        note.location_name,
        note.location_type,
        note.business_note_title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchWords.every((word) => combinedText.includes(word));
    });
  }, [publicNotes, searchTerm]);

  // Sort notes
  const sortedNotes = useMemo(() => {
    const notes = [...filteredNotes];

    if (sortBy === "newest") {
      // Most recent
      return notes.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (sortBy === "oldest") {
      // Oldest
      return notes.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    } else if (sortBy === "location") {
      // By location
      return notes.sort((a, b) =>
        a.location_type.localeCompare(b.location_type),
      );
    }

    return notes;
  }, [filteredNotes, sortBy]);

  return (
    <>
      {noteActionsWithNotes.length > 0 ? (
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <aside
            className={`w-full md:w-64 lg:w-72 bg-bg-brand-secondary-default border-b md:border-b-0 md:border-r border-border-token-neutral-minimal overflow-y-auto flex-shrink-0 ${showMobileMenu ? "flex py-6 max-h-[50vh]" : "hidden md:flex md:py-6"
              }`}
            aria-label={i18n.get("typeFilter")}
          >
            <nav className="w-full px-4">
              <ul className="relative">
                {/* Continuous vertical guide line â€” scoped to list height */}
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#deddf7]"></div>

                {noteActionsWithNotes.map((action) => {
                  const isActive = selectedActionTitle === action.title;
                  return (
                    <li key={action.title}>
                      <button
                        type="button"
                        onClick={() => scrollToActionSection(action.title)}
                        aria-current={isActive ? 'true' : undefined}
                        className={`w-full flex items-center justify-between pl-4 pr-2 py-3 mb-1 relative transition-colors focus-blue ${
                          isActive
                            ? "text-fg-brand-subtle"
                            : "text-fg-neutral-strong hover:text-fg-brand-subtle"
                        }`}
                      >
                        {/* Active indicator line (overlays the guide line) */}
                        {isActive && (
                          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-bg-brand-primary-default"></div>
                        )}

                        <span className="label-m-medium">{action.title}</span>

                        <CaretRightIcon
                          size={16}
                          weight="bold"
                          aria-hidden="true"
                          className={isActive ? "text-fg-brand-subtle" : ""}
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
            {/* Mobile Menu Button - Only show when sidebar is collapsed */}
            {!showMobileMenu && sortedNotes.length > 0 && (
              <div className="md:hidden mb-4">
                <button
                  onClick={() => setShowMobileMenu(true)}
                  className="flex items-center gap-2 text-fg-brand-subtle font-medium"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                  Menu
                </button>
              </div>
            )}

            {/* Top Action Bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
              {/* Left: Search + Results (desktop) */}
              <div className="flex items-center gap-4">
                <div className="relative w-full md:w-64">
                  <label htmlFor="public-content-search" className="sr-only">
                    {i18n.get("searchAlerts")}
                  </label>
                  <input
                    id="public-content-search"
                    type="text"
                    placeholder={i18n.get("searchAlerts")}
                    value={localSearchTerm}
                    onChange={(e) => {
                      setLocalSearchTerm(e.target.value);
                      AnalyticsService.trackGAEventHandler("alertsTabSearchPerformed");
                      AnalyticsService.trackMx("alertsTabSearchPerformed", { SearchTerm: e.target.value });
                    }}
                    className="w-full h-12 px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal text-base md:text-[13px] text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {localSearchTerm ? (
                    <button
                      type="button"
                      onClick={() => setLocalSearchTerm("")}
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-fg-neutral-minimal hover:text-fg-neutral-subtle focus-blue"
                    >
                      <XIcon size={20} aria-hidden="true" />
                    </button>
                  ) : (
                    <Search
                      size={20}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap hidden md:inline">
                  {sortedNotes.length}{" "}
                  {sortedNotes.length === 1 ? i18n.get("result") : i18n.get("results")}
                </span>
              </div>

              {/* Right: Results (mobile) + Sort Dropdown */}
              <div className="flex items-center justify-between md:justify-end md:gap-2">
                <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap md:hidden">
                  {sortedNotes.length}{" "}
                  {sortedNotes.length === 1 ? i18n.get("result") : i18n.get("results")}
                </span>

                {/* Sort Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
                    <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue">
                      <span className="underline">
                        {sortBy === "newest" && i18n.get("dateAddedNewest")}
                        {sortBy === "oldest" && i18n.get("dateAddedOldest")}
                        {sortBy === "location" && i18n.get("byLocation")}
                      </span>
                      <CaretDownIcon size={16} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel aria-label="Sort by">
                      {i18n.get("sortBy")}:
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={sortBy}
                      onValueChange={(value) => {
                        setSortBy(value as "newest" | "oldest" | "location");
                        AnalyticsService.trackGAEventHandler("alertsTabSortChanged");
                        AnalyticsService.trackMx("alertsTabSortChanged", { SortBy: value });
                      }}
                    >
                      <DropdownMenuRadioItem value="newest">
                        {i18n.get("dateAddedNewest")}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="oldest">
                        {i18n.get("dateAddedOldest")}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="location">
                        {i18n.get("byLocation")}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Grouped Notes by Action Title */}

            <div className="space-y-8">
              {noteActionsWithNotes.map((action) => {
                const actionNotes = sortedNotes.filter(
                  (note) => note.action_title === action.title,
                );
                const sectionId = `action-section-${action.title.toLowerCase().replace(/\s+/g, "-")}`;

                return (
                  <section
                    key={action.title}
                    id={sectionId}
                    className="space-y-4"
                  >
                    {/* Section Header */}
                    <h3 className="title-s-semibold text-fg-neutral-strong">
                      {action.title} ({actionNotes.length})
                    </h3>

                    {/* Notes List */}
                    {actionNotes.length > 0 ? (
                      <div className="space-y-4">
                        {actionNotes.map((note) => {
                          const roles = note.business_note_by_user_roles;
                          const authorName = isMapper
                            ? note.business_note_by_user
                            : roles?.isSociabilityMapper || roles?.isSociabilityTeam
                              ? "Sociability team"
                              : roles
                                ? note.business_note_by_user
                                : "Sociability team";
                          const dateRange =
                            note.formatedScheduleDateFrom && note.formatedScheduleDateTo
                              ? `${note.formatedScheduleDateFrom} to ${note.formatedScheduleDateTo}`
                              : "";

                          return (
                            <article
                              key={note.business_note_text_id}
                              className="bg-bg-brand-secondary-default rounded-lg border border-border-token-neutral-minimal p-4"
                            >
                              <div className="mb-2">
                                <p className="label-s-medium">
                                  {dateRange && (
                                    <>
                                      <span className="text-blue-600">{dateRange}</span>
                                      <span> • </span>
                                    </>
                                  )}
                                  <span className="text-fg-neutral-strong">
                                    Added on {note.formatedCreatedAt} by {authorName}
                                  </span>
                                </p>
                              </div>
                              {note.business_note_title && (
                                <h4 className="label-m-semibold text-fg-neutral-strong mb-2">
                                  {note.business_note_title}
                                </h4>
                              )}
                              {note.location_name && (
                                <div className="flex items-center gap-1 mb-2">
                                  <MapPinIcon size={14} aria-hidden="true" className="text-gray-500 flex-shrink-0" />
                                  <span className="label-s-regular text-gray-600">{note.location_name}</span>
                                </div>
                              )}
                              {note.business_note_text && (
                                <p className="label-s-regular text-fg-neutral-strong">
                                  {note.business_note_text}
                                </p>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>

          </main>
        </div>
      ) : (
        <>
          <div className="h-screen flex flex-col">
            {/* Secondary Filter Tabs */}
            <nav
              className="bg-white border-b border-border-token-neutral-minimal px-4 py-4 md:px-6 lg:px-8 flex-shrink-0"
              aria-label="Review filters"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                {/* Left: Search + Results (desktop) */}
                <div className="flex items-center gap-4">
                  <div className="relative w-full md:w-64">
                    <label htmlFor="public-empty-search" className="sr-only">
                      {i18n.get("searchAlerts")}
                    </label>
                    <input
                      id="public-empty-search"
                      type="text"
                      placeholder={i18n.get("searchAlerts")}
                      value={localSearchTerm}
                      onChange={(e) => {
                        setLocalSearchTerm(e.target.value);
                        AnalyticsService.trackGAEventHandler("alertsTabSearchPerformed");
                        AnalyticsService.trackMx("alertsTabSearchPerformed", { SearchTerm: e.target.value });
                      }}
                      className="w-full h-12 px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal text-base md:text-[13px] text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {localSearchTerm ? (
                      <button
                        type="button"
                        onClick={() => setLocalSearchTerm("")}
                        aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-fg-neutral-minimal hover:text-fg-neutral-subtle focus-blue"
                      >
                        <XIcon size={20} aria-hidden="true" />
                      </button>
                    ) : (
                      <Search
                        size={20}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap hidden md:inline">
                    0 {i18n.get("results")}
                  </span>
                </div>

                {/* Right: Results (mobile) + Sort Dropdown */}
                <div className="flex items-center justify-between md:justify-end md:gap-2">
                  <span className="label-m-medium text-fg-neutral-subtle whitespace-nowrap md:hidden">
                    0 {i18n.get("results")}
                  </span>

                  {/* Sort Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
                      <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue">
                        <span className="underline">
                          {sortBy === "newest" && i18n.get("dateAddedNewest")}
                          {sortBy === "oldest" && i18n.get("dateAddedOldest")}
                          {sortBy === "location" && i18n.get("byLocation")}
                        </span>
                        <CaretDownIcon size={16} />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel aria-label="Sort by">
                        {i18n.get("sortBy")}:
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={sortBy}
                        onValueChange={(value) => {
                          setSortBy(value as "newest" | "oldest" | "location");
                          AnalyticsService.trackGAEventHandler("alertsTabSortChanged");
                          AnalyticsService.trackMx("alertsTabSortChanged", { SortBy: value });
                        }}
                      >
                        <DropdownMenuRadioItem value="newest">
                          {i18n.get("dateAddedNewest")}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="oldest">
                          {i18n.get("dateAddedOldest")}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="location">
                          {i18n.get("byLocation")}
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </nav>

            {/* No reviews found Content */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              <div className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
                <div className="text-center py-12" role="status" aria-live="polite">
                  <div className="flex flex-1 flex-col justify-start sm:justify-center items-center px-4 pt-8 sm:pt-0">
                    <FolderDottedIcon size={32} className="text-fg-neutral-strong" aria-hidden="true" />
                    <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                        {i18n.get("alerts")}
                      </p>
                      <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                        {searchTerm
                          ? i18n.get("noNotesFoundMatchingSearch")
                          : i18n.get("noPublicNotesAvailable")}
                      </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default PublicContent;
